"use client";
import React, { useState } from 'react';

const CareerApplicationForm = () => {
    const [fileName, setFileName] = useState("No file chosen");

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFileName(file.name);
        } else {
            setFileName("No file chosen");
        }
    };

    return (
        <section id="application-form" className="w-full max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 scroll-mt-24">
            <div className="mb-10 text-center">
                <h2 className="text-[32px] md:text-[40px] font-bold text-[#112140] mb-4 leading-tight">
                    Join Our Team
                </h2>
                <p className="text-[16px] md:text-[18px] text-[#475467] max-w-[600px] mx-auto">
                    We are always looking for talented individuals to help us redefine precision labeling.
                    Fill out the form below to apply.
                </p>
            </div>

            <form className="bg-[#F7F9FC] rounded-[24px] shadow-[0_12px_28px_rgba(16,24,40,0.08)] p-6 sm:p-10 md:p-12 flex flex-col gap-6 md:gap-8">

                {/* Personal Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    <div className="flex flex-col gap-2">
                        <label htmlFor="fullName" className="font-semibold text-[#232B38] text-[15px]">
                            Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="fullName"
                            type="text"
                            required
                            placeholder="Enter your full name"
                            className="h-12 border border-[#E6E9F0] rounded-lg px-4 text-base bg-white placeholder-[#98A2B3] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label htmlFor="email" className="font-semibold text-[#232B38] text-[15px]">
                            Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="email"
                            type="email"
                            required
                            placeholder="yourname@example.com"
                            className="h-12 border border-[#E6E9F0] rounded-lg px-4 text-base bg-white placeholder-[#98A2B3] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label htmlFor="phone" className="font-semibold text-[#232B38] text-[15px]">
                            Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="phone"
                            type="tel"
                            required
                            placeholder="+91 98765 43210"
                            className="h-12 border border-[#E6E9F0] rounded-lg px-4 text-base bg-white placeholder-[#98A2B3] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label htmlFor="position" className="font-semibold text-[#232B38] text-[15px]">
                            Applying For <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <select
                                id="position"
                                required
                                defaultValue=""
                                className="w-full h-12 border border-[#E6E9F0] rounded-lg px-4 text-base bg-white text-[#232B38] appearance-none outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all invalid:text-[#98A2B3]"
                            >
                                <option value="" disabled>Select a department</option>
                                <option value="production">Production & Manufacturing</option>
                                <option value="quality">Quality Control</option>
                                <option value="design">Design & Pre-press</option>
                                <option value="sales">Sales & Marketing</option>
                                <option value="operations">Operations & Logistics</option>
                                <option value="other">Other</option>
                            </select>
                            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#7D8CA4]">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Resume & LinkedIn */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    <div className="flex flex-col gap-2">
                        <label htmlFor="linkedin" className="font-semibold text-[#232B38] text-[15px]">
                            LinkedIn Profile / Portfolio
                        </label>
                        <input
                            id="linkedin"
                            type="url"
                            placeholder="https://linkedin.com/in/..."
                            className="h-12 border border-[#E6E9F0] rounded-lg px-4 text-base bg-white placeholder-[#98A2B3] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label htmlFor="resume" className="font-semibold text-[#232B38] text-[15px]">
                            Resume / CV <span className="text-red-500">*</span>
                        </label>
                        <div className="relative group">
                            <input
                                id="resume"
                                type="file"
                                required
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                accept=".pdf,.doc,.docx"
                                onChange={handleFileChange}
                            />
                            <div className="h-12 border border-[#E6E9F0] rounded-lg flex items-center bg-white w-full transition-all group-hover:border-blue-400 group-hover:bg-blue-50/10">
                                <span className="pl-4 pr-12 text-base text-[#5A6782] truncate flex-1 leading-[48px]">
                                    {fileName}
                                </span>
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#F0F2F5] border border-[#E0E4EB] text-[#5A6782] group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                                    </span>
                                </span>
                            </div>
                        </div>
                        <span className="text-xs text-[#7D8CA4] mt-1">Accepted formats: PDF, DOC, DOCX (Max 5MB)</span>
                    </div>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-2">
                    <label htmlFor="message" className="font-semibold text-[#232B38] text-[15px]">
                        Cover Letter / Message
                    </label>
                    <textarea
                        id="message"
                        placeholder="Tell us why you'd be a great fit..."
                        className="min-h-[140px] border border-[#E6E9F0] rounded-lg p-4 text-base bg-white placeholder-[#98A2B3] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all resize-y"
                    ></textarea>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center mt-4">
                    <button
                        type="submit"
                        className="w-full sm:w-auto min-w-[200px] bg-[#112140] hover:bg-[#0F1A34] text-white font-bold py-4 px-8 rounded-full text-[16px] transition-all duration-300 shadow-[0_4px_14px_rgba(17,33,64,0.3)] hover:shadow-[0_6px_20px_rgba(17,33,64,0.25)] hover:scale-[1.02] active:scale-[0.98]"
                    >
                        Submit Application
                    </button>
                </div>

            </form>
        </section>
    );
};

export default CareerApplicationForm;
